import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import HttpRoutingService from "../../shared/services/httpRoutingService";
import { API } from "../../shared/apiRoutes";
import { createPosRoleProps, deleteRoleProps, getAllPosPermissionResProps, getOneRoleResponse, rolesResponseProps, updatePosRoleProps, updateValueProps } from "../../components/role/roleModule";
import { ZenToast } from "zen-core-elements";

//type
interface initialState {
    loading: boolean,
    value: rolesResponseProps | null,
    isReached: boolean,
    lastSearch: string | null,
    delete: deleteRoleProps | null,
    create: createPosRoleProps | null,
    update: updatePosRoleProps | null,
    roleMenuPermission: getAllPosPermissionResProps | null,
    onePosRole: getOneRoleResponse | null,
    error: string | null,

}
const initialState: initialState = {
    loading: false,
    value: null,
    isReached: false,
    lastSearch: null,
    delete: null,
    create: null,
    update: null,
    roleMenuPermission: null,
    onePosRole: null,
    error: "",
}

export const getAllRoles = createAsyncThunk("roles/getAllRoles", ({ storeId }: { storeId: number }) => {
    const url = HttpRoutingService?.replaceUrl(API?.getAllRoles, { storeId: storeId })
    return HttpRoutingService?.getMethod<rolesResponseProps>(url)
})
export const deletePosRole = createAsyncThunk(
    "roles/deletePosRole",
    async ({ storeId, id }: { storeId: number; id: number }, { rejectWithValue }) => {
        try {
            const url = HttpRoutingService?.replaceUrl(API?.deletePosRole, { storeId, id });
            return await HttpRoutingService?.deleteMethod<deleteRoleProps>(url, undefined, "v2/");
        } catch (error: any) {
            // error is already unwrapped in your interceptor
            // forward backend payload to reducers
            return rejectWithValue(error);
        }
    }
);

export const createRole = createAsyncThunk("roles/createPosRole", ({ storeId, data }: { storeId: number, data: updateValueProps }) => {
    const url = HttpRoutingService?.replaceUrl(API?.createPosRole, { storeId: storeId })
    return HttpRoutingService?.postMethod<createPosRoleProps>(url, data, {}, "v2/")
})
export const updateRole = createAsyncThunk("roles/updatePosRole", ({ storeId, id, data }: { storeId: number, id: number, data: updateValueProps }) => {
    const url = HttpRoutingService?.replaceUrl(API?.updatePosRole, { storeId: storeId, id: id })
    return HttpRoutingService?.putMethod<updatePosRoleProps>(url, data, {}, 'v2/')
})
export const roleMenuPermission = createAsyncThunk("roles/getAllPosRoleMenuPermission", ({ storeId }: { storeId: number }) => {
    const url = HttpRoutingService?.replaceUrl(API?.getAllPosRoleMenuPermission, { storeId: storeId })
    return HttpRoutingService?.getMethod<getAllPosPermissionResProps>(url)
})
export const onePosRole = createAsyncThunk("roles/getOnePosRole", ({ storeId, id }: { storeId: number, id: number }) => {
    const url = HttpRoutingService?.replaceUrl(API?.getOnePosRole, { storeId: storeId, id: id })
    return HttpRoutingService?.getMethod<getOneRoleResponse>(url)
})

export const rolesSlice = createSlice({
    name: "roles",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getAllRoles?.pending, (state) => {
                state.loading = true
            })
            .addCase(getAllRoles?.fulfilled, (state, action) => {
                state.loading = false
                const incoming = action.payload
                const offset = (action.meta.arg as any)?.data?.offset ?? 0
                const searchText = (action.meta.arg as any)?.data?.searchText ?? ''
                if (offset === 0) {
                    // Fresh fetch (initial / search / refresh) — replace everything
                    state.value = incoming
                    state.lastSearch = searchText
                } else {
                    // Pagination append
                    state.value = {
                        ...incoming,
                        roles: {
                            count: incoming.roles.count,
                            rows: [
                                ...(state.value?.roles?.rows ?? []),
                                ...incoming.roles.rows,
                            ],
                        },
                    }
                }
                // No more pages when received rows bring total up to count
                const totalLoaded = offset === 0
                    ? incoming.roles.rows.length
                    : (state.value?.roles?.rows?.length ?? 0)
                state.isReached = totalLoaded >= incoming.roles.count
            })
            .addCase(getAllRoles?.rejected, (state, action) => {
                state.loading = false
                state.error = action?.error?.message as string
                ZenToast({ type: 'error', message: 'Failed to load roles. Please try again.' });
            })
            .addCase(deletePosRole?.pending, (state) => {
                state.loading = true
            })
            .addCase(deletePosRole?.fulfilled, (state, action) => {
                state.loading = false
                state.delete = action.payload
            })
            .addCase(deletePosRole?.rejected, (state, action) => {
                state.loading = false
                state.error = action?.error?.message as string
            })
            .addCase(createRole?.pending, (state) => {
                state.loading = true
            })
            .addCase(createRole?.fulfilled, (state, action) => {
                state.loading = false
                state.create = action.payload
            })
            .addCase(createRole?.rejected, (state, action) => {
                state.loading = false
                state.error = action?.error?.message as string
            })
            .addCase(updateRole?.pending, (state) => {
                state.loading = true
            })
            .addCase(updateRole?.fulfilled, (state, action) => {
                state.loading = false
                state.update = action.payload
            })
            .addCase(updateRole?.rejected, (state, action) => {
                state.loading = false
                state.error = action?.error?.message as string
            })
            .addCase(roleMenuPermission?.pending, (state) => {
                state.loading = true
            })
            .addCase(roleMenuPermission?.fulfilled, (state, action) => {
                state.loading = false
                state.roleMenuPermission = action.payload
            })
            .addCase(roleMenuPermission?.rejected, (state, action) => {
                state.loading = false
                state.error = action?.error?.message as string
            })
            .addCase(onePosRole?.pending, (state) => {
                state.loading = true
            })
            .addCase(onePosRole?.fulfilled, (state, action) => {
                state.loading = false
                state.onePosRole = action.payload
            })
            .addCase(onePosRole?.rejected, (state, action) => {
                state.loading = false
                state.error = action?.error?.message as string
            })
    }
})

export default rolesSlice?.reducer